import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { PageHeader, DarkPanel, btnOnDark } from '../components/ui';
import { fmtLocal } from '../utils/time';

export default function RateApp() {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    api.get('/ratings/me')
      .then((r) => {
        if (r.data?.rated) {
          setExisting(r.data);
          setStars(r.data.stars || 0);
          setComment(r.data.comment || '');
        }
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    if (stars < 1 || stars > 5) {
      setError('Please select 1 to 5 stars');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/ratings', { stars, comment });
      setMsg(res.data?.message || 'Thanks for your rating!');
      setExisting({
        rated: true,
        stars,
        comment,
        updatedAt: res.data?.updatedAt,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not submit rating');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pb-8">
      <PageHeader title="Rate the app" subtitle="How would you rate StockXignal? Your feedback helps us improve." />

      {error && (
        <div className="mb-4 rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{error}</div>
      )}
      {msg && (
        <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{msg}</div>
      )}

      <DarkPanel title="Your rating">
      <form onSubmit={submit} className="space-y-5">
        <div>
          <div className="flex gap-1.5" role="radiogroup" aria-label="Star rating">
            {[1, 2, 3, 4, 5].map((n) => {
              const active = (hover || stars) >= n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setStars(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  className={`text-3xl leading-none transition ${
                    active ? 'text-amber-400' : 'text-slate-600 hover:text-amber-200'
                  }`}
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                >
                  ★
                </button>
              );
            })}
          </div>
          {stars > 0 && (
            <p className="mt-2 text-sm text-slate-400">{stars} of 5 stars</p>
          )}
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-400">Comment (optional)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 1000))}
            rows={3}
            className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:ring-1 focus:ring-emerald-400/60"
            placeholder="What do you like or what can we improve?"
          />
        </div>

        <button type="submit" disabled={loading || stars < 1} className={`${btnOnDark} w-full py-2.5`}>
          {loading ? 'Submitting…' : existing?.rated ? 'Update rating' : 'Submit rating'}
        </button>

        {existing?.updatedAt && (
          <p className="text-center text-[11px] text-slate-500">
            Last updated {fmtLocal(existing.updatedAt)}
          </p>
        )}
      </form>
      </DarkPanel>
    </div>
  );
}
